import { CUSTOMIZE_WEBINAR_ROUTE } from '@anm/constants/routes';
import isNil from 'lodash/fp/isNil';
import { useEffect, useState } from 'react';
import { CustomizationTabIndexes, dashboardTabMap } from '../../../../pages/video';
import { tabMap, WEBINAR_TABS } from '../../../../pages/webinar';

import routes from '../../../../routes';

type UseLeaveAlertProps<T> = {
  webinarId?: string;
  projectId?: string;
  isPending: boolean;
  onActiveButtonChange?: (index: T) => void;
};

const { Router } = routes;

const useLeaveAlert = <T extends CustomizationTabIndexes | WEBINAR_TABS>({
  projectId,
  webinarId,
  isPending,
  onActiveButtonChange
}: UseLeaveAlertProps<T>) => {
  const [targetTab, setTargetTab] = useState<T | null>(null);
  const [showLeaveAlert, setShowLeaveAlert] = useState(false);
  const [prevAlertState, setPrevAlertState] = useState(showLeaveAlert);
  const [prevPending, setPrevPending] = useState(isPending);

  useEffect(() => {
    if (isPending === prevPending) return;

    if (isPending) {
      setPrevPending(isPending);
      return;
    }

    setShowLeaveAlert(false);
  }, [isPending]);

  useEffect(() => {
    const wasAlertClosed = showLeaveAlert === prevAlertState;

    if (wasAlertClosed || isNil(targetTab)) return;

    openTab(targetTab);
  }, [showLeaveAlert]);

  const openTab = (tab: T) => {
    onActiveButtonChange?.(tab);

    if (projectId) {
      Router.pushRoute('video', { projectId, tab: dashboardTabMap[tab] }, { shallow: true });
    }

    if (webinarId) {
      const selectedTab = Object.keys(tabMap).find(o => tabMap[o].index === tab);

      Router.pushRoute(
        CUSTOMIZE_WEBINAR_ROUTE,
        { webinarId, ...(selectedTab && { tab: selectedTab }) },
        { shallow: true }
      );
    }
  };

  return [openTab, setTargetTab, showLeaveAlert, setPrevAlertState, setShowLeaveAlert] as const;
};

export default useLeaveAlert;
